import { onError } from '@apollo/client/link/error';
import { Observable } from '@apollo/client/utilities';

import { emitForceLogout } from '@/auth/authEvents';
import { getValidAccessToken } from '@/auth/refreshCoordinator';

/**
 * Global GraphQL/network error handling (docs/development/FrontendGuidelines.md §9.2). Transport/auth errors
 * (an `UNAUTHENTICATED`-coded GraphQL error, or a 401 network error) are handled here — a single
 * retry after a silent refresh — never in individual screens. Business errors (a mutation's
 * `Payload.errors` array) are NOT touched here; those are 200 OK responses handled at the call
 * site, not transport failures.
 */
export const errorLink = onError(({ graphQLErrors, networkError, operation, forward }) => {
  const isUnauthenticated =
    graphQLErrors?.some(
      (error) =>
        error.extensions?.code === 'AUTH_NOT_AUTHENTICATED' ||
        error.extensions?.code === 'UNAUTHENTICATED',
    ) ??
    (networkError && 'statusCode' in networkError && networkError.statusCode === 401);

  const alreadyRetried = Boolean(operation.getContext().__isRetry);

  // A second 401 after a refresh attempt means the session cannot continue — log out rather than
  // loop (docs/development/FrontendGuidelines.md §6.2 point 4).
  if (isUnauthenticated && alreadyRetried) {
    emitForceLogout('refresh-failed');
    return undefined;
  }

  if (isUnauthenticated) {
    return new Observable((observer) => {
      getValidAccessToken({ forceRefresh: true })
        .then((token) => {
          if (!token) {
            observer.error(networkError ?? graphQLErrors?.[0] ?? new Error('Not authenticated'));
            return;
          }
          operation.setContext({ __isRetry: true });
          forward(operation).subscribe({
            next: (result) => observer.next(result),
            error: (err: unknown) => observer.error(err),
            complete: () => observer.complete(),
          });
        })
        .catch((err: unknown) => observer.error(err));
    });
  }

  if (graphQLErrors) {
    for (const { message, path } of graphQLErrors) {
      console.error(`[GraphQL error] ${message} at ${path?.join('.') ?? 'unknown path'}`);
    }
  }
  if (networkError) {
    console.error(`[Network error] ${networkError.message}`);
  }
  return undefined;
});
